import { useState, useMemo } from "react";
import { cameras, Camera, Brand, CameraType, LensType } from "@/data/cameras";

export interface Filters {
  brand: Brand | "Todas";
  megapixels: number[];
  type: CameraType[];
  lensType: LensType | "Todas";
  irMin: number;
  hasWifi: boolean | null;
  has4G: boolean | null;
  hasSolar: boolean | null;
  hasAudio: boolean | null;
  hasColorNight: boolean | null;
  hasDualLight: boolean | null;
  hasIK10: boolean | null;
  wdrType: "WDR" | "DWDR" | null;
  detPeople: boolean | null;
  detVehicle: boolean | null;
  hasLPR: boolean | null;
  searchQuery: string;
}

export const defaultFilters: Filters = {
  brand: "Todas", megapixels: [], type: [], lensType: "Todas", irMin: 0,
  hasWifi: null, has4G: null, hasSolar: null, hasAudio: null,
  hasColorNight: null, hasDualLight: null, hasIK10: null,
  wdrType: null, detPeople: null, detVehicle: null, hasLPR: null, searchQuery: "",
};

export type SortKey = "model" | "megapixels" | "irRange" | "brand" | "price";

export function useComparador() {
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sortKey, setSortKey] = useState<SortKey>("model");
  const [modelInput, setModelInput] = useState("");
  const [equivalentResults, setEquivalentResults] = useState<Camera[]>([]);
  const [searchedCamera, setSearchedCamera] = useState<Camera | null>(null);
  const [noEquivalentFound, setNoEquivalentFound] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Camera | null>(null);

  const filteredCameras = useMemo(() => {
    let result = cameras.filter(cam => {
      if (cam.megapixels === 0) return false;
      if (filters.brand !== "Todas" && cam.brand !== filters.brand) return false;
      if (filters.megapixels.length > 0 && !filters.megapixels.includes(cam.megapixels)) return false;
      if (filters.type.length > 0 && !filters.type.includes(cam.type)) return false;
      if (filters.lensType !== "Todas" && cam.lensType !== filters.lensType) return false;
      if (filters.irMin > 0 && cam.irRange < filters.irMin) return false;
      if (filters.hasWifi !== null && cam.hasWifi !== filters.hasWifi) return false;
      if (filters.has4G !== null && cam.has4G !== filters.has4G) return false;
      if (filters.hasSolar !== null && cam.hasSolar !== filters.hasSolar) return false;
      if (filters.hasAudio !== null && cam.hasAudio !== filters.hasAudio) return false;
      if (filters.hasColorNight !== null && cam.hasColorNight !== filters.hasColorNight) return false;
      if (filters.hasDualLight !== null && cam.hasDualLight !== filters.hasDualLight) return false;
      if (filters.hasIK10 !== null && cam.hasIK10 !== filters.hasIK10) return false;
      if (filters.wdrType !== null && cam.wdrType !== filters.wdrType) return false;
      if (filters.detPeople !== null && cam.detPeople !== filters.detPeople) return false;
      if (filters.detVehicle !== null && cam.detVehicle !== filters.detVehicle) return false;
      if (filters.hasLPR !== null && cam.hasLPR !== filters.hasLPR) return false;
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        if (!cam.model.toLowerCase().includes(q) && !cam.fullName.toLowerCase().includes(q) && !cam.ref.toLowerCase().includes(q)) return false;
      }
      return true;
    });

    result.sort((a, b) => {
      switch (sortKey) {
        case "megapixels": return b.megapixels - a.megapixels;
        case "irRange": return b.irRange - a.irRange;
        case "brand": return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        case "price": {
          const pa = parseFloat(a.price.replace(/[€\s.]/g, '').replace(',', '.')) || 0;
          const pb = parseFloat(b.price.replace(/[€\s.]/g, '').replace(',', '.')) || 0;
          return pa - pb;
        }
        default: return a.model.localeCompare(b.model);
      }
    });
    return result;
  }, [filters, sortKey]);

  const modelSuggestions = useMemo(() => {
    if (!modelInput || modelInput.length < 2) return [];
    const q = modelInput.toLowerCase();
    return cameras.filter(c =>
      c.model.toLowerCase().includes(q) || c.fullName.toLowerCase().includes(q) || c.ref.toLowerCase().includes(q)
    ).slice(0, 10);
  }, [modelInput]);

  const searchEquivalent = (camera?: Camera) => {
    const source = camera || selectedModel;
    if (!source) return;
    setSearchedCamera(source);
    const otherBrand = source.brand === "DAHUA" ? "TVT" : "DAHUA";
    const explicitEquivs = source.equivalent || [];
    const explicitCams = explicitEquivs.map(m => cameras.find(c => c.model === m || c.model.includes(m))).filter((c): c is Camera => c !== undefined);
    const reverseEquivs = cameras.filter(c =>
      c.brand === otherBrand &&
      c.equivalent?.some(eq => eq === source.model || source.model.includes(eq) || eq.includes(source.model))
    );
    const allEquivs = [...new Map([...explicitCams, ...reverseEquivs].map(c => [c.id, c])).values()];
    if (allEquivs.length > 0) {
      setEquivalentResults(allEquivs);
      setNoEquivalentFound(false);
    } else {
      const similar = cameras.filter(c =>
        c.brand === otherBrand && c.megapixels === source.megapixels && c.lensType === source.lensType && c.type === source.type
      ).slice(0, 3);
      setEquivalentResults(similar);
      setNoEquivalentFound(similar.length === 0);
    }
  };

  const selectModelFromSuggestion = (cam: Camera) => {
    setModelInput(cam.model);
    setSelectedModel(cam);
    setEquivalentResults([]);
    setSearchedCamera(null);
    setNoEquivalentFound(false);
  };

  const resetEquivalentSearch = () => {
    setModelInput("");
    setEquivalentResults([]);
    setSearchedCamera(null);
    setSelectedModel(null);
    setNoEquivalentFound(false);
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => setFilters(defaultFilters);
  const totalCameras = useMemo(() => cameras.filter(c => c.megapixels > 0).length, []);

  return {
    filters, updateFilter, resetFilters,
    sortKey, setSortKey, filteredCameras,
    modelInput, setModelInput, modelSuggestions,
    equivalentResults, searchedCamera, selectedModel, noEquivalentFound,
    searchEquivalent, selectModelFromSuggestion, resetEquivalentSearch,
    totalCameras,
  };
}
